import { AnalyticsEventDispatcher } from '../AnalyticsEventDispatcher'

import { EventDispatcherConfig } from '../types'

import {
  AnalyticsManifestDispatcher,
  CustomEventDispatchers,
  CustomEventDispatcherOptions,
  CustomEventDispatcherConstructor,
  AnalyticsManifestWithInit,
} from '../types/analytics-manifest'

import { createManifestDispatcher } from './create-manifest-dispatcher'

/**
 * Static analytics manifest configuration
 *
 * @returns analytics manifest ready to be used
 *
 * @see AnalyticsManifestDispatcher
 */
export function createAnalyticsManifest<C extends CustomEventDispatchers>(
  staticDispatcherConfig: EventDispatcherConfig,
  customEventsDispatcher?: CustomEventDispatcherConstructor<C>,
): AnalyticsManifestDispatcher<C>

/**
 * Dynamic analytics manifest configuration
 *
 * @returns analytics manifest with initialization hook method
 *
 * @see AnalyticsManifestWithInit
 */
export function createAnalyticsManifest<
  C extends CustomEventDispatchers,
  T extends unknown[],
>(
  initDispatcherConfig: (...args: T) => EventDispatcherConfig,
  customEventsDispatcher?: CustomEventDispatcherConstructor<C>,
): AnalyticsManifestWithInit<C, T>

/**
 * Helper to create an Analytics Manifest
 *
 * See `docs/ANALYTICS.md` file in the root for more details.
 *
 * @param configDispatcher - Constant or function configuration for the events of a page
 * @param customEventDispatchers - Custom event dispatchers which might require more data than initial config
 * @returns An analytics manifest for the page, which includes the default events of the dispatcher alongside the
 * custom events, and an initialization hook in case of dynamic configuration.
 */
export function createAnalyticsManifest<
  C extends CustomEventDispatchers,
  T extends unknown[],
>(
  configDispatcher:
    | EventDispatcherConfig
    | ((...args: T) => EventDispatcherConfig),
  customEventDispatchers?: (
    customEventDispatcherOptions: CustomEventDispatcherOptions,
  ) => C,
) {
  if ('config' in configDispatcher) {
    return createManifestDispatcher(configDispatcher, customEventDispatchers)
  }

  /** Casting due dynamic initialization. See: {@link AnalyticsInitFunc.init} */
  const ref: { analyticsManifest: AnalyticsEventDispatcher & C } = {
    analyticsManifest: {} as AnalyticsEventDispatcher & C,
  }

  const init = (...args: T) => {
    const analyticsManifest = createManifestDispatcher(
      configDispatcher(...args),
      customEventDispatchers,
    )

    Object.assign(ref.analyticsManifest, analyticsManifest)
  }

  return Object.assign(ref.analyticsManifest, {
    init,
  })
}
